import React, { useCallback } from 'react';
import { useDropzone, DropzoneOptions } from 'react-dropzone';
import { Info } from 'lucide-react';
import { FileDropzoneProps, SystemConfig } from './types';

const FileDropzone: React.FC<FileDropzoneProps> = ({ systemConfig, onFilesDropped }) => {
  // Parse allowed file formats and max file size from system config
  const getAllowedFileFormats = () => {
    const formats = systemConfig.find(item => item.Id === 'allowed_file_formats')?.ConfigValue || '';
    const formatArray = formats.split(',').map(format => format.trim().toLowerCase());
    
    // Create accept object for dropzone
    const acceptObject: { [key: string]: string[] } = {};
    formatArray.forEach(format => {
      // Map file extensions to MIME types
      if (format.startsWith('.')) {
        const ext = format;
        // Add common MIME types for supported formats
        switch (ext.toLowerCase()) {
          case '.docx':
            acceptObject['application/vnd.openxmlformats-officedocument.wordprocessingml.document'] = [ext];
            break;
          case '.pdf':
            acceptObject['application/pdf'] = [ext];
            break;
          case '.txt':
            acceptObject['text/plain'] = [ext];
            break;
          case '.pptx':
            acceptObject['application/vnd.openxmlformats-officedocument.presentationml.presentation'] = [ext];
            break;
          case '.html':
          case '.htm':
            acceptObject['text/html'] = [ext];
            break;
          case '.jpeg':
          case '.jpg':
            acceptObject['image/jpeg'] = [ext];
            break;
          case '.png':
            acceptObject['image/png'] = [ext];
            break;
          case '.bmp':
            acceptObject['image/bmp'] = [ext];
            break;
          case '.heif':
            acceptObject['image/heif'] = [ext];
            break;
          case '.tiff':
          case '.tif':
            acceptObject['image/tiff'] = [ext];
            break;
          case '.xlsx':
          case '.xls':
            acceptObject['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] = [ext];
            break;
          case '.csv':
            acceptObject['text/csv'] = [ext];
            break;
          case '.flv':
          case '.asf':
          case '.wmv':
          case '.qt':
          case '.mov':
          case '.mpg':
          case '.mpeg':
          case '.mpe':
          case '.avi':
          case '.m4v':
          case '.mp4':
          case '.3gp':
            acceptObject['video/*'] = [ext];
            break;
          default:
            // For unknown types, use a generic binary type
            acceptObject['application/octet-stream'] = [ext];
        }
      }
    });
    return acceptObject;
  };

  const getMaxFileSize = () => {
    const maxSize = systemConfig.find(item => item.Id === 'max_allowed_file_size')?.ConfigValue;
    return maxSize ? parseInt(maxSize) * 1024 * 1024 : 500 * 1024 * 1024; // Default to 500MB if not specified
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getMaxFileSizeDisplay = () => {
    const maxSize = getMaxFileSize();
    return formatFileSize(maxSize);
  };

  const onDrop = useCallback<NonNullable<DropzoneOptions['onDrop']>>(async (acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      console.log('Some files were rejected:', rejectedFiles);
      return;
    }
    
    onFilesDropped(acceptedFiles);
  }, [onFilesDropped]);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    multiple: true,
    accept: getAllowedFileFormats(),
    maxSize: getMaxFileSize(),
  });

  return (
    <div
      {...getRootProps()}
      className={`dropzone w-full h-36 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer transition-colors
        ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
        ${fileRejections.length > 0 ? 'border-red-500' : ''}`}
    >
      <input {...getInputProps()} />
      <div className="text-center p-4">
        {isDragActive ? (
          <p className="text-blue-600">Drop the files here</p>
        ) : (
          <div className="space-y-2">
            <p className="text-gray-600">Drag and drop files here, or click to select files</p>
            <div className="flex items-center justify-center space-x-2">
              <p className="text-sm text-gray-500">
                Supports all common document, image, video, and presentation formats (max {getMaxFileSizeDisplay()})
              </p>
              <div className="relative group">
                <Info className="h-4 w-4 text-gray-500 cursor-pointer" />
                <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block bg-white border border-gray-300 rounded-lg shadow-lg p-2 text-left text-sm text-gray-700 w-64">
                  <p><strong>Documents:</strong> DOCX, PDF, TXT</p>
                  <p><strong>Images:</strong> JPEG, JPG, PNG, BMP, HEIF, TIFF</p>
                  <p><strong>Presentations:</strong> PPTX</p>
                  <p><strong>Spreadsheets:</strong> XLSX, XLS, CSV</p>
                  <p><strong>Videos:</strong> FLV, ASF, WMV, QT, MOV, MPG, MPEG, MPE, AVI, M4V, MP4, 3GP</p>
                  <p><strong>Others:</strong> HTML, HTM</p>
                </div>
              </div>
            </div>
            {fileRejections.length > 0 && (
              <p className="text-sm text-red-500">
                Some files were rejected. Please check the file type and size limits.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileDropzone;